import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {BottomSheet} from 'react-spring-bottom-sheet';
import {toast} from 'react-toastify';
import Button from '../../../components/button';
import CustomMessageArea from '../../../components/customMessageArea';
import HeaderBasketButton from '../../../components/headerBasketButton';
import ShopDetailsAdvertisementsCard from '../../../components/mazraeKit/cards/primaryCard/shopDetailsAdvertisementsCard';
import ShopDetailCard from '../../../components/mazraeKit/cards/shopDetailCard';
import CustomBadge from '../../../components/mazraeKit/customBadge';
import CustomCarousel from '../../../components/mazraeKit/customCarousel';
import ShareTitleHeader from '../../../components/mazraeKit/headers/shareTitleHeader';
import NoItemFoundPage from '../../../components/noItemFoundPage';
import PageLoader from '../../../components/pageLoader';
import InputField from '../../../components/simpleInput';
import SpinnerLoading from '../../../components/SpinnerLoading';
import useGetAdLikeStatus from '../../../services/hookService/getAdLikeStatus/useGetAdLikeStatus';
import useGetAdverisements from '../../../services/hookService/getAdvertisements/useGetAdverisements';
import useGetCategoriesCardDetail from '../../../services/hookService/getCategoryCardDetail/useGetCategoriesCardDetail';
import usePatchPostLike from '../../../services/hookService/patchPostLike/usePatchPostLike';
import usePostBrandStoreOrder from '../../../services/hookService/postBrandStoreOrder/usePostBrandStoreOrder';
import {RootState} from '../../../store/redux';
import {CodeToInputGroupName} from '../../../utilities/helper/codeToInputGroupName';
import {googleAnalyticsInstance} from '../../../utilities/helper/googleAnalitics';
import {getQuantityPackUnitByValue} from '../../../utilities/helper/unitGenerator/unit.types';
import BasketItemEditButton from '../../shopping-basket/components/BasketItemEditButton';
import {SaleMethodChoices} from '../productShopsList/helper';
import {DescriptionView} from './descriptionView';

type displayAdvertisementType =
  | 'personal' // personal ads
  | 'normal-shop' // ads for stores that are not brand or lend tech
  | 'brand-shop' // ads for stores that has brand and is request sale method
  | 'lendTech-shop'; // ads for stores that offer installment, credit, and cash sales (payment is handled)

const CategoryCardDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {inputGroup, guid} = useParams() as {
    inputGroup: string;
    guid: string;
  };

  const state = useSelector(
    (state: RootState) => state.inputGroupAdvertisement,
  );

  const {useGetCardDetail} = useGetCategoriesCardDetail();
  const {data, isLoading} = useGetCardDetail(guid, inputGroup);
  const {
    mutate: sendOrder,
    isLoading: isOrderRegisterLoading,
    isSuccess,
  } = usePostBrandStoreOrder();

  const [showBottomSheet, setShowBottomSheet] = useState<boolean>(false);
  const [orderAmount, setOrderAmount] = useState<string>('');
  const {adLikeStatusData, refetch} = useGetAdLikeStatus(
    data?.id?.toString() || '',
    !!data?.id,
    'ads',
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const trueQuantity = Math.abs(
    (data?.hold_quantity ?? 0) - (data?.available_quantity ?? 0),
  );

  const {advertisementList: similarProducts} = useGetAdverisements(
    {
      advertisement_type: inputGroup,
      input_guid: data?.input?.guid,
      exclude_advertisement_id: guid,
    },
    !!data?.input?.guid,
  );

  function descriptionArray(inputGroup: string) {
    if (!data) return null;

    const attrList = (arr?: {attribute: string}[]) =>
      arr?.map(i => i?.attribute)?.toString() || '';

    const commonOverview = {title: 'قیمت:', titleInfo: ''};

    // نوع محصول که میشه نوع فرمولاسیون
    const generators: Record<string, () => any> = {
      fertilizer: () => {
        const d = data as ICommonCardDetail;
        const productData = [
          {name: 'نوع محصول:', field: d?.sub_input?.title},
          {name: 'تاریخ تولید:', field: d?.produce_year},
          {name: 'تاریخ انقضا:', field: d?.expire_year},
          {
            name: 'وزن بسته بندی:',
            field: d?.quantity_pack
              ? `${d?.quantity_pack} ${
                  d?.quantity_pack_unit &&
                  getQuantityPackUnitByValue(d?.quantity_pack_unit)?.title
                }`
              : null,
          },
          {name: 'کشور تولید کننده:', field: d?.producing_country?.name},
          {name: 'ویژگی ها:', field: attrList(d?.attributes)},
        ];
        if (d?.brand?.name)
          productData.push({
            name: 'برند:',
            field: d.brand.name,
          });
        return {
          productData,
          productOverview: commonOverview,
        };
      },

      pesticide: () => generators?.fertilizer(),
      seed: () => generators?.fertilizer(),

      employee: () => {
        const d = data as IEmployeeCardDetail;
        return {
          productData: [
            {name: 'نام و نام خانوادگی:', field: d?.name},
            {name: 'سن:', field: d?.age},
            {
              name: 'جنسیت:',
              field:
                d?.gender === 'male'
                  ? 'آقا'
                  : d?.gender === 'female'
                  ? 'خانم'
                  : '',
            },
            {
              name: 'ساعت فعالیت:',
              field:
                d?.from_hour && d?.to_hour
                  ? `${d?.from_hour} الی ${d?.to_hour}`
                  : '',
            },
            {name: 'سابقه کار:', field: d?.job_experience},
          ],
          productOverview: {
            title: 'دستمزد:',
            titleInfo: d?.price_unit === 'hourly' ? 'ساعتی' : 'روزانه',
          },
        };
      },

      tool: () => {
        const d = data as IToolsCardDetail;
        return {
          productData: [
            {name: 'نوع محصول:', field: d?.sub_input?.title},
            {name: 'تاریخ تولید:', field: d?.produce_year},
            {name: 'شرکت سازنده:', field: d?.company},
            {name: 'کشور سازنده:', field: d?.producing_country?.name},
            {name: 'ویژگی ها:', field: attrList(d?.attributes)},
          ],
          productOverview: commonOverview,
        };
      },

      machinery: () => {
        const d = data as IMachineryDetail;

        // Codes 11 & 17 share same structure
        if (d?.input?.code === 11 || d?.input?.code === 17) {
          return {
            productData: [
              {name: 'نوع محصول:', field: d?.sub_input?.title},
              {name: 'تاریخ تولید:', field: d?.produce_year},
              {name: 'کشور سازنده:', field: d?.producing_country?.name},
              {name: 'ویژگی ها:', field: attrList(d?.attributes)},
            ],
            productOverview: commonOverview,
          };
        }

        // Default machinery version
        const defaultMachineryProductData = [
          {name: 'زمان آزاد:', field: d?.producing_country?.name},
        ];
        if (d.service) {
          defaultMachineryProductData.push({
            name: 'خدمات:',
            field: d?.service?.map(i => i).join(' ، '),
          });
        }
        return {
          productData: defaultMachineryProductData,
          productOverview: {
            title: 'قیمت:',
            titleInfo: d?.price_unit === 'per_hour' ? 'در ساعت' : 'در هکتار',
          },
        };
      },

      farm: () => {
        const d = data as IFarmDetails;
        return {
          productData: [
            {name: 'مساحت:', field: d?.existing},
            {name: 'نوع آبیاری:', field: [d?.watering_system?.title]},
            {name: 'امکانات:', field: attrList(d?.attributes)},
          ],
          productOverview: commonOverview,
        };
      },

      lab: () => ({
        productData: [],
        productOverview: commonOverview,
      }),
    };

    return generators[inputGroup]?.() ?? null;
  }

  // const handleBack = () => {
  //   dispatch(
  //     findAdvertisementList({
  //       ...state,
  //       input_guid: [],
  //     }),
  //   );
  //   navigate(-1);

  //   googleAnalyticsInstance?.eventGoogleAnalytics?.({
  //     category: 'shareLink',
  //     action: 'کاربر روی اشتراک گداری لینک از هدر کلیک کرد ',
  //   });
  // };

  const {editLikePost} = usePatchPostLike({
    onSuccessAction: () => {
      refetch();
    },
  });

  const handleOrderAmountChange = (value: string) => {
    if (value === '') {
      setOrderAmount('');
      return;
    }

    setOrderAmount(value?.replace?.(/\D/g, ''));
  };

  const handleOrderSubmit = () => {
    if (!data?.min_sell_quantity) return;

    if (Number(orderAmount) < data?.min_sell_quantity!) {
      toast.error(`مقدار حداقل سفارش ${data?.min_sell_quantity} است`);
      return;
    }

    sendOrder({advertisement: guid, quantity: orderAmount});
  };

  useEffect(() => {
    if (!data?.min_sell_quantity) return;
    setOrderAmount(String(data?.min_sell_quantity));
  }, [data?.min_sell_quantity]);

  // Generate images data for Carousel, from user images (images) or admin images (sub_brand images)
  const imagesData = (data: ICommonCardDetail) => {
    const userImages =
      data?.images?.map(item => ({
        image: item?.image_330 || item?.image,
        link: '',
        id: item?.id,
        isExternal: false,
      })) ?? [];

    const adminImages =
      data?.sub_brand?.images?.map(item => ({
        image: item?.image,
        id: item?.id,
        link: '',
        isExternal: false,
      })) ?? [];

    return [...adminImages, ...userImages];
  };

  // true quantity >= the request quantity which is min_sell_quantity
  const canAddOrder = () =>
    Math.abs((data?.basket?.quantity ?? 0) - (data?.hold_quantity ?? 0)) >=
    (data?.min_sell_quantity ?? 0);

  if (!data?.id && !isLoading) return <NoItemFoundPage />;

  if (isLoading) return <PageLoader />;

  return (
    <div className="flex flex-col w-full h-full">
      <ShareTitleHeader
        title={data?.title}
        isLiked={adLikeStatusData?.like_status || false}
        onClickLike={() => {
          data?.id &&
            editLikePost({id: data?.id?.toString(), liking_type: 'ads'});
        }}
        rightExtra={<HeaderBasketButton />}
      />
      <div
        ref={scrollContainerRef}
        className="flex overflow-y-auto flex-col w-full h-full"
      >
        <div className="px-2 pt-3 w-full">
          <CustomCarousel
            data={imagesData(data as ICommonCardDetail)}
            customClassSlider="!rounded-none !m-0"
          />
        </div>
        <div className="flex items-center px-3 mt-3 text-md">
          <div className="flex gap-2 items-center">
            <span className="w-28 font-medium text-gray-700">
              {descriptionArray(inputGroup)?.productOverview?.title}
            </span>
            {data?.price_on_call ? (
              <span>لطفا تماس بگیرید</span>
            ) : (
              <RenderPrice
                discount_price={data?.discount_price}
                ad_price={data?.ad_price || undefined}
                discount_amount={data?.discount_amount}
                has_discount={data?.has_discount}
              />
            )}
            {descriptionArray(inputGroup)?.productOverview?.titleInfo && (
              <CustomBadge
                title={
                  descriptionArray(inputGroup)?.productOverview?.titleInfo || ''
                }
                hasRemoveItem={false}
              />
            )}
          </div>
        </div>
        {Boolean(trueQuantity) && (
          <div className="flex items-center px-3 mt-3 text-lg font-medium">
            <div className="flex gap-2 items-center">
              <span className="w-28 text-gray-700 text-md">موجودی:</span>
            </div>
            <div className="flex gap-2 justify-end text-xl text-neutral-600">
              <span className="flex gap-1 items-center">
                <span>{trueQuantity}</span>
                <span>عدد</span>
              </span>
            </div>
          </div>
        )}
        {Boolean(data?.min_sell_quantity) && (
          <div className="flex items-center px-3 mt-3 text-lg font-medium">
            <div className="flex gap-2 items-center">
              <span className="w-28 text-gray-700 text-md">حداقل خرید:</span>
            </div>
            <div className="flex gap-2 justify-end text-neutral-600">
              <span className="flex gap-1 items-center text-xl text-neutral-600">
                <span>{data?.min_sell_quantity}</span>
                <span>عدد</span>
              </span>
            </div>
          </div>
        )}
        {descriptionArray(inputGroup)?.productData?.some(
          (item: descriptionArrayData) => Boolean(item?.field),
        ) && (
          <>
            <div className="mt-3 w-full text-gray-100 border-t"></div>
            <div className="flex flex-col px-3">
              <h5 className="mt-3 mb-4 text-sm text-neutral-600">
                مشخصات محصول{' '}
              </h5>
              <div className="flex flex-col gap-5 pb-7 w-full text-xl">
                {descriptionArray(inputGroup)?.productData?.map(
                  (item: descriptionArrayData, index: number) =>
                    displayDescription(item, index),
                )}
              </div>
            </div>
          </>
        )}
        {(data?.description ||
          data?.combinations ||
          data?.application_method) && (
          <DescriptionView
            data={{
              description: data?.description ?? '',
              combinations: data?.combinations ?? '',
              application_method: data?.application_method ?? '',
            }}
          />
        )}
        <div>
          {similarProducts?.results && similarProducts?.results.length > 0 && (
            <>
              <div className="mt-3 w-full text-gray-100 border-t"></div>
              <h5 className="px-3 mt-6 mb-4 font-medium text-gray-700 text-md">
                محصولات مشابه
              </h5>
              <div className="w-full flex gap-4 h-[170px] items-center  px-3 overflow-x-auto ">
                {similarProducts?.results?.map(
                  (item: IInputGroupsAdvertisementResults, index: number) => (
                    <ShopDetailsAdvertisementsCard
                      key={item?.id}
                      {...item}
                      onClick={() => {
                        navigate(
                          `/category-card-detail/${
                            CodeToInputGroupName[item?.input_group?.code]
                          }/${item?.id}`,
                        );
                        scrollContainerRef.current?.scrollTo({
                          top: 0,
                          behavior: 'smooth',
                        });
                      }}
                    />
                  ),
                )}
              </div>
            </>
          )}
          {data?.store && advertisementType(data) !== 'personal' && (
            <>
              <div className="mt-3 w-full text-gray-100 border-t"></div>
              <div
                className="px-3 mt-8"
                onClick={() => {
                  // @ts-ignore
                  navigate(`/shop-card-detail/${data?.store?.guid || ''}`, {
                    state: {
                      advertisement_guid: guid,
                      advertisement_inputGroup: inputGroup,
                    },
                  });
                  googleAnalyticsInstance?.eventGoogleAnalytics?.({
                    category: 'detailShop',
                    action: 'کاربر روی جزییات شاپ از جزییات آگهی کلیک کرد',
                  });
                }}
              >
                <ShopDetailCard {...data?.store} />
              </div>
            </>
          )}
          <div className="py-3 mt-8 w-full border-t">
            <div
              className="flex gap-2 px-4"
              onClick={() => navigate(`/violation-report-store/${guid}`)}
            >
              <i className="text-[1.5rem] icon-namereport-2 text-red-1000" />
              <span className="flex justify-center items-center text-center text-red-1000 text-md">
                گزارش تخلف
              </span>
            </div>
            <div className="mt-3 w-full text-gray-100 border-t"></div>
            <div
              className="flex gap-2 px-4 my-4"
              onClick={() => navigate('/rules')}
            >
              <i className="text-[1.5rem] icon-namelaw text-farm-shop-500" />
              <span className="flex justify-center items-center text-center">
                قوانین و شرایط استفاده از فروشگاه
              </span>
            </div>
          </div>
        </div>
      </div>

      {(advertisementType(data) === 'personal' ||
        advertisementType(data) === 'normal-shop') && (
        <div className="flex w-full p-[12px] shadow-dropShadow rounded-top-left-1 rounded-top-right-1 border-top">
          <CustomMessageArea
            phoneNumber={data?.hide_phone_number}
            productId={String(data?.id)}
            isShop={true}
            shopNumber={data?.phone_number}
            supplierGuid={data?.store?.supplier_guid ?? data?.supplier_guid}
          />
        </div>
      )}

      {advertisementType(data) === 'brand-shop' && (
        <div className="flex px-4 mt-auto mb-4 w-full h-14 bg-white">
          <button
            type="button"
            className="w-full text-white rounded-lg bg-farm-shop-500 disabled:bg-gray-300 disabled:text-gray-700"
            onClick={() => setShowBottomSheet(true)}
            disabled={!canAddOrder}
          >
            سفارش محصول
          </button>
        </div>
      )}
      {advertisementType(data) === 'lendTech-shop' && (
        <div className="flex justify-center items-center px-4 mt-auto mb-4 w-full h-14 bg-white">
          <BasketItemEditButton
            variant="lg"
            min_sell_quantity={data?.min_sell_quantity ?? 0}
            available_quantity={data?.available_quantity ?? 0}
            hold_quantity={data?.hold_quantity ?? 0}
            basket_quantity={data?.basket?.quantity ?? 0}
            basket_guid={data?.basket?.guid as string}
            advertisement_id={data?.id as string}
            price={data?.ad_price}
          />
        </div>
      )}

      {/* order bottom sheet */}
      <BottomSheet
        onDismiss={() => {
          setShowBottomSheet(false);
        }}
        open={showBottomSheet}
        blocking={true}
        className="custom-bottom-sheet"
      >
        <div className="flex relative flex-col gap-3 px-6 py-2 w-full CalenderWrapperBottomSheet-wrapper">
          <i
            className="absolute top-6 left-6 text-2xl text-gray-500 icon-nameClose-remove"
            onClick={() => setShowBottomSheet(false)}
          />
          {isSuccess ? (
            <>
              <div className="flex flex-col justify-center items-center my-2">
                <i className="text-3xl icon-nameCheck-circle text-farm-shop-500"></i>
                <span className="font-semibold text-md">ثبت سفارش</span>
              </div>
              <span className="px-3 font-light leading-6 text-md">
                ثبت سفارش شما با موفقیت انجام شد. بعد از بررسی توسط تامین کننده
                با شما تماس خواهند گرفت.
              </span>
              <div className="flex justify-between items-center p-3 w-full">
                <div className="flex justify-end w-full">
                  <Button
                    className="flex gap-2 items-center w-full text-white border-none outline-none bg-farm-shop-500"
                    onClick={() => {
                      setShowBottomSheet(false);
                    }}
                  >
                    {isOrderRegisterLoading ? <SpinnerLoading /> : 'تایید'}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="flex justify-center p-0 mt-4 w-full text-lg text-gray-950 font-Regular">
                تعداد محصول
              </p>
              <div className="flex justify-between px-3 w-full">
                <InputField
                  // added this to avoid react spring auto focus
                  tabIndex={-1}
                  label="تعداد"
                  name="price"
                  type="number"
                  inputMode="numeric"
                  required
                  disableAutoFocus
                  value={orderAmount}
                  onValueChange={handleOrderAmountChange}
                />
              </div>

              <div className="flex justify-between items-center p-3 w-full">
                <div className="flex justify-end w-full">
                  <Button
                    className="flex gap-2 items-center w-full text-white border-none outline-none bg-farm-shop-500"
                    onClick={handleOrderSubmit}
                  >
                    {isOrderRegisterLoading ? (
                      <SpinnerLoading size={15} />
                    ) : (
                      'تایید'
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </BottomSheet>
    </div>
  );
};

export default CategoryCardDetail;

const RenderPrice = ({
  discount_price,
  ad_price,
  discount_amount,
  has_discount,
}: IPriceProps) => {
  const originalPrice = Number(ad_price);
  const discountedPrice = Number(discount_price);
  const discountPercent = Number(discount_amount);

  const hasValidOriginalPrice =
    Number.isFinite(originalPrice) && originalPrice > 0;

  const hasValidDiscountPrice =
    Number.isFinite(discountedPrice) &&
    discountedPrice > 0 &&
    discountedPrice < originalPrice;

  const hasValidDiscountAmount =
    Number.isFinite(discountPercent) && discountPercent > 0;

  const showDiscount =
    has_discount &&
    hasValidOriginalPrice &&
    hasValidDiscountPrice &&
    hasValidDiscountAmount;

  const finalPrice = showDiscount ? discountedPrice : originalPrice;

  if (!hasValidOriginalPrice) {
    return null;
  }

  const formatPrice = (price: number) => Math.round(price).toLocaleString();

  return (
    <div className="flex items-center text-xs font-medium text-gray-700">
      <div className="flex flex-col text-left">
        {showDiscount && (
          <span className="flex gap-1 items-center text-xl line-through text-neutral-600">
            <span className="text-xl">{formatPrice(originalPrice)}</span>

            <span className="text-md">تومان</span>
          </span>
        )}

        <div className="flex gap-1 items-center">
          {showDiscount && (
            <div className="flex justify-center items-center p-2 w-6 h-5 text-white bg-red-500 rounded">
              {discountPercent}%
            </div>
          )}

          <span className="flex gap-1 items-center text-xl text-neutral-600">
            <span>{formatPrice(finalPrice)}</span>

            <span className="text-md">تومان</span>
          </span>
        </div>
      </div>
    </div>
  );
};

type DescriptionItem = {
  name: string;
  field?: string | number | string[] | null | undefined;
};
const displayDescription = (item: DescriptionItem, index: number) => {
  return item?.field ? (
    <div className="flex items-center text-lg font-medium" key={index}>
      <div className="flex gap-2 items-center">
        <span className="w-28 text-gray-700 text-md">{item?.name}</span>
      </div>
      <div className="flex gap-2 justify-end text-md text-neutral-600">
        <span>{item?.field}</span>
      </div>
    </div>
  ) : null;
};

export const advertisementType = (
  data: any,
): displayAdvertisementType | null => {
  const isPersonalAd = data?.ad_type === 'personal';

  if (isPersonalAd) return 'personal';

  const saleMethods = SaleMethodChoices(
    data?.sale_method ?? data?.store?.sale_method,
  );

  const hasBrand = Boolean(data?.store?.is_sale_agent || data?.is_sale_agent);
  const hasCredit =
    saleMethods?.isDirect || saleMethods?.isDirectAndInstallment;

  switch (true) {
    // Normal shop: no brand + no credit
    case !hasBrand && !hasCredit:
      return 'normal-shop';

    // Brand shop: has brand + is request type sale method
    case hasBrand && saleMethods?.isSendRequest:
      return 'brand-shop';

    // Lend-tech shop: credit option regardless of brand
    case hasCredit:
      return 'lendTech-shop';

    default:
      return null;
  }
};
